// Adapted from https://github.com/drizzle-team/drizzle-orm/issues/2605#issuecomment-3674089897
//
// Connection-level query logger: wraps an expo-sqlite-compatible connection so
// every query logs its type/table/duration. The formatting and the suppression
// key are shared with the vendored drizzle prepared query (see
// `expo-sqlite-async/log-format` and `expo-sqlite-async/logger-context`) so the
// Drizzle-threaded `logger(import.meta.location)` path produces identical output and
// suppresses this wrapper's duplicate log for queries it handles itself.
import {
  formatQueryLine,
  getQueryType,
  parseSqliteExplain,
  shouldLogExecutionPlan,
  shouldLogQuery,
  type ExplainStats,
} from "@-/com.npmjs.drizzle-orm/expo-sqlite-async/log-format";
import {
  decrementSuppressionDepth,
  getDatabaseNameTag,
  getSuppressionDepth,
  incrementSuppressionDepth,
  setDatabaseNameTag,
} from "@-/com.npmjs.drizzle-orm/expo-sqlite-async/logger-context";
import { hasLength } from "@-/com.npmjs.ts-array-length";
import { trackQuery } from "#slow-query-report";

const DATABASE_LOGGER = "__logger";
const DATABASE_WRAPPED = "__haniverseDatabaseLoggerWrapped";
const STATEMENT_WRAPPED = "__haniverseDatabaseLoggerStatementWrapped";

const methodsToWrap = [
  "runAsync",
  "getAllAsync",
  "getFirstAsync",
  "getEachAsync",
  "execAsync",
] as const;
const preparedExecuteMethodsToWrap = ["executeAsync", "executeForRawResultAsync"] as const;

type UnknownFunction = (...args: unknown[]) => unknown;

function isDevelopment(): boolean {
  return typeof __DEV__ !== "undefined" && __DEV__;
}

function getQueryText(args: readonly unknown[]): string {
  const queryText = args[0];
  return typeof queryText === "string" ? queryText : "";
}

function isUnknownFunction(value: unknown): value is UnknownFunction {
  return typeof value === "function";
}

function isAsyncIterable(value: unknown): value is AsyncIterable<unknown> {
  return (
    typeof value === "object" &&
    value !== null &&
    isUnknownFunction(Reflect.get(value, Symbol.asyncIterator))
  );
}

function isExplainRowArray(value: unknown): value is { detail?: unknown }[] {
  return Array.isArray(value) && value.every((row) => typeof row === "object" && row !== null);
}

async function getExplainStats(
  db: object,
  queryText: string,
  bindArgs: readonly unknown[],
): Promise<ExplainStats | null> {
  const getAllAsync = Reflect.get(db, "getAllAsync");
  if (!isUnknownFunction(getAllAsync)) {
    return null;
  }

  try {
    const rows = await Reflect.apply(getAllAsync, db, [
      `EXPLAIN QUERY PLAN ${queryText}`,
      ...bindArgs,
    ]);
    if (!isExplainRowArray(rows) || hasLength(rows, 0)) {
      return null;
    }
    return parseSqliteExplain(rows);
  } catch {
    return null;
  }
}

async function logQuery(
  db: object,
  queryText: string,
  bindArgs: readonly unknown[],
  duration: number,
  nativeMicros: number | null,
  logger: Console,
  callsite: string | null,
): Promise<void> {
  if (!isDevelopment() || !shouldLogQuery(queryText)) {
    return;
  }

  const explainStats =
    shouldLogExecutionPlan() && getQueryType(queryText) === "SELECT"
      ? await getExplainStats(db, queryText, bindArgs)
      : null;

  const line = formatQueryLine(
    queryText,
    duration,
    explainStats,
    nativeMicros,
    getDatabaseNameTag(db),
    bindArgs,
  );
  // A threaded `logger(import.meta.location)` already prefixes its own `[file:line:col]`.
  // Queries that reach the raw connection WITHOUT one (init/seed paths, or a
  // handle queried directly rather than through `db(import.meta.location)`) print
  // unprefixed — `callsite` is only set on platforms where the stack yields a
  // real app frame (never under Hermes; see `captureCallsite`).
  logger.log(callsite === null ? line : `${callsite} ${line}`);
}

function isLogger(obj: unknown): obj is Console {
  return typeof obj === "object" && obj !== null && "debug" in obj;
}

// Read the native-time accumulator surfaced by the patched expo-sqlite (see
// patches/expo-sqlite@56.0.4.patch). Returns null when the patch is not
// applied (e.g. tests with a mocked db, or after a future expo-sqlite bump
// drops the field), so the logger falls back to wall-clock only.
function readNativeMicros(db: object): number | null {
  const value = Reflect.get(db, "__haniverseLastNativeMicros");
  return typeof value === "number" ? value : null;
}

// Pulls the first app-code frame out of a captured stack so an un-threaded query
// still names where it came from. Skips this module, the vendored drizzle
// driver, and node_modules so the reported frame is the caller's own code.
const STACK_FRAME_RE = /\(?((?:[\w.-]+\/)*[\w.-]+\.(?:tsx?|jsx?|cjs|mjs)):(\d+):(\d+)\)?/;
// `InternalBytecode.js` is Hermes's synthetic frame for bytecode-compiled
// functions (not real source), so its presence means the stack is unusable for
// recovering an app callsite.
const STACK_SKIP_RE =
  /node_modules|InternalBytecode|create-performance-logger|expo-sqlite-async|drizzle-orm/;

function captureCallsite(): string | null {
  const { stack } = new Error("capture-callsite");
  if (typeof stack !== "string") {
    return null;
  }
  for (const rawLine of stack.split("\n").slice(1)) {
    if (STACK_SKIP_RE.test(rawLine)) {
      continue;
    }
    const match = rawLine.match(STACK_FRAME_RE);
    if (match === null) {
      continue;
    }
    const file = match[1].split("/").at(-1) ?? match[1];
    return `[${file}:${match[2]}:${match[3]}]`;
  }
  return null;
}

interface PoppedLogger {
  callsite: string | null;
  logger: Console;
}

function popLogger(db: object, ...args: unknown[]): PoppedLogger {
  const fromDatabase = Reflect.get(db, DATABASE_LOGGER);
  if (isLogger(fromDatabase)) {
    return { callsite: null, logger: fromDatabase };
  }

  const lastArg = args.at(-1);
  if (isLogger(lastArg)) {
    args.pop();
    return { callsite: null, logger: lastArg };
  }

  // No threaded logger: fall back to bare `console`, but recover a callsite from
  // the stack (dev only) so the line isn't anonymous.
  return {
    callsite: isDevelopment() ? captureCallsite() : null,
    logger: console,
  };
}

function wrapGetEachAsync(db: object, originalMethod: UnknownFunction): UnknownFunction {
  return (...args: unknown[]) => {
    const { callsite, logger } = popLogger(db, ...args);
    const queryText = getQueryText(args);
    const bindArgs = args.slice(1);
    const startTime = Date.now();
    const result = Reflect.apply(originalMethod, db, args);

    if (!isAsyncIterable(result)) {
      throw new TypeError("Expected getEachAsync to return an async iterable");
    }

    if (!isDevelopment() || !shouldLogQuery(queryText)) {
      return result;
    }

    return (async function* loggedRows() {
      try {
        for await (const row of result) {
          yield row;
        }
      } finally {
        await logQuery(
          db,
          queryText,
          bindArgs,
          Date.now() - startTime,
          readNativeMicros(db),
          logger,
          callsite,
        );
      }
    })();
  };
}

function wrapAsyncQueryMethod(db: object, originalMethod: UnknownFunction): UnknownFunction {
  return async (...args: unknown[]) => {
    const { callsite, logger } = popLogger(db, ...args);
    const queryText = getQueryText(args);
    const bindArgs = args.slice(1);
    const startTime = Date.now();

    // When the vendored drizzle prepared query runs the round trip, it has
    // already bumped the suppression depth and will emit the line itself with
    // the threaded callsite. Capture the depth BEFORE our own increment (which
    // only silences nested prepared statements) so we skip the duplicate.
    const suppressed = getSuppressionDepth(db) > 0;

    // Slow/stuck reporting is deliberately OUTSIDE the `isDevelopment()` gate that
    // guards `logQuery` — staging and production are the environments we cannot
    // otherwise see into. Tracked even when `suppressed`, because suppression only
    // dedupes the human-readable log line; the round trip being timed is real.
    const settleTracking = trackQuery(() => ({
      callsite,
      databaseName: getDatabaseNameTag(db),
      operation: getQueryType(queryText),
      queryText,
    }));

    incrementSuppressionDepth(db);
    let result: unknown;
    try {
      result = await Reflect.apply(originalMethod, db, args);
      settleTracking("resolved");
    } catch (error) {
      settleTracking("rejected");
      throw error;
    } finally {
      decrementSuppressionDepth(db);
    }

    if (!suppressed) {
      await logQuery(
        db,
        queryText,
        bindArgs,
        Date.now() - startTime,
        readNativeMicros(db),
        logger,
        callsite,
      );
    }
    return result;
  };
}

function wrapPreparedStatement(db: object, statement: unknown, queryText: string): unknown {
  if (typeof statement !== "object" || statement === null) {
    return statement;
  }

  if (Reflect.get(statement, STATEMENT_WRAPPED) === true) {
    return statement;
  }
  Reflect.set(statement, STATEMENT_WRAPPED, true);

  for (const method of preparedExecuteMethodsToWrap) {
    const originalMethod = Reflect.get(statement, method);
    if (!isUnknownFunction(originalMethod)) {
      continue;
    }

    Reflect.set(statement, method, async (...args: unknown[]) => {
      const { callsite, logger } = popLogger(db, ...args);
      const startTime = Date.now();

      // See the note in `wrapAsyncQueryMethod`: this runs in every environment,
      // and this is the path drizzle's prepared queries actually take.
      const settleTracking = trackQuery(() => ({
        callsite,
        databaseName: getDatabaseNameTag(db),
        operation: getQueryType(queryText),
        queryText,
      }));

      let result: unknown;
      try {
        result = await Reflect.apply(originalMethod, statement, args);
        settleTracking("resolved");
      } catch (error) {
        settleTracking("rejected");
        throw error;
      }
      if (getSuppressionDepth(db) === 0) {
        // For prepared-statement paths, the underlying NativeStatement is the
        // one whose counter we want. expo-sqlite's `SQLiteStatement` exposes
        // it via `getLastNativeMicros()` (patched); fall back to reading the
        // DB-level field if present, else null. See
        // patches/expo-sqlite@56.0.4.patch.
        const statementGetter = Reflect.get(statement, "getLastNativeMicros");
        const nativeMicros =
          typeof statementGetter === "function"
            ? (Reflect.apply(statementGetter, statement, []) as number)
            : readNativeMicros(db);
        await logQuery(db, queryText, args, Date.now() - startTime, nativeMicros, logger, callsite);
      }
      return result;
    });
  }

  return statement;
}

function wrapPrepareAsync(db: object): void {
  const originalMethod = Reflect.get(db, "prepareAsync");
  if (!isUnknownFunction(originalMethod)) {
    return;
  }

  Reflect.set(db, "prepareAsync", async (...args: unknown[]) => {
    const statement = await Reflect.apply(originalMethod, db, args);
    return wrapPreparedStatement(db, statement, getQueryText(args));
  });
}

/**
 * Creates a performance-logging wrapper for an Expo SQLite-compatible database.
 *
 * In development, every wrapped query logs its type, table, simple WHERE-column
 * hint, and duration. SQLite `EXPLAIN QUERY PLAN` scan/index details for SELECT
 * queries are included by default in dev (a slow full-table scan is flagged with
 * a loud `⚠ SLOW SCAN` highlight); set `EXPO_PUBLIC_DB_LOG_EXECUTION_TIME=false`
 * to silence them, or `=true` to force them on outside dev.
 *
 * `databaseName` (e.g. `"actorDb"` / `"contentDb"` / `"staticDb"`) is stamped on
 * the connection so every query line is prefixed with the source database
 * instead of the generic `[sqlite]` — see `formatQueryLine`. Stamped even when
 * the connection is already wrapped so a re-open through the memo still carries
 * the tag.
 */
export function createPerformanceLogger<Database extends object>(
  db: Database,
  databaseName?: string,
): Database {
  if (databaseName !== undefined) {
    setDatabaseNameTag(db, databaseName);
  }
  if (Reflect.get(db, DATABASE_WRAPPED) === true) {
    return db;
  }
  Reflect.set(db, DATABASE_WRAPPED, true);

  for (const method of methodsToWrap) {
    const originalMethod = Reflect.get(db, method);
    if (!isUnknownFunction(originalMethod)) {
      continue;
    }

    Reflect.set(
      db,
      method,
      method === "getEachAsync"
        ? wrapGetEachAsync(db, originalMethod)
        : wrapAsyncQueryMethod(db, originalMethod),
    );
  }

  wrapPrepareAsync(db);
  return db;
}
